import {
    Chat,
    Construction,
    DescriptionOutlined,
    DesignServices,
    DeviceHub,
    Help,
    Home,
    People,
    PermContactCalendar,
    RotateLeft,
    Search,
} from '@mui/icons-material';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import type { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useMyFields } from '/@/hooks/fields';
import {
    PROFILES,
    useIsValidatedBayerUserType,
    useIsValidatedConsultant,
    useIsValidatedSquad,
    useValidatedType,
} from '/@/hooks/session';
import { _ } from '/@/locale';
import { getAskAnExpertEnabled } from '/@/store/ask-an-expert/selectors';
import { getBagQualityEnabled } from '/@/store/bag-quality/selectors';
import { getCarbonCalculatorEnabled } from '/@/store/carbon-calculator/selectors';
import { getFieldTrialsNetworkEnabled } from '/@/store/field-trial-network/selectors';
import {
    getActivitiesEnabled,
    getEnabledWeedFinder,
    getHelpCenterEnabled,
    getSessionCountry,
} from '/@/store/session/selectors';
import { openWhatsappWeb } from '/@/utils';
import { reverse } from '/@/utils/router';
import Icon from '../../icon';

export type { NavbarTabKey } from '/@/hooks/session';

export const useTabs = ({
    setUtilitiesOpen,
    setOpen,
}: {
    setUtilitiesOpen: Dispatch<SetStateAction<boolean>>;
    setOpen?: Dispatch<SetStateAction<boolean>>;
}): typeof tabs => {
    const navigate = useNavigate();
    const validatedType = useValidatedType();
    const isValidatedBayerUserType = useIsValidatedBayerUserType();
    const helpCenterEnabled = useSelector(getHelpCenterEnabled);

    const tabs = {
        home: {
            label: _('Inicio'),
            icon: <Home />,
            onClick: () => {
                setOpen?.(false);
                navigate(reverse('NewHome'));
            },
            enabled: true,
        },
        growers: {
            label: _('Productores'),
            icon: <People />,
            onClick: () => {
                setOpen?.(false);
                navigate(reverse('Growers.list'));
            },
            enabled: true,
        },
        rvs: {
            label: _('RVs'),
            icon: <People />,
            onClick: () => {
                setOpen?.(false);
                navigate(reverse('RVs.list'));
            },
            enabled: true,
        },
        fields: {
            label: _('Mis Lotes'),
            icon: <AutoAwesomeMosaicOutlinedIcon />,
            onClick: () => {
                setOpen?.(false);
                navigate(reverse('MyFields.list'));
            },
            enabled: true,
        },
        reports: {
            label: _('Reportes'),
            icon: <DescriptionOutlined />,
            onClick: () => {
                setOpen?.(false);
                navigate(reverse('reports.List'));
            },
            enabled: true,
        },
        recommendations: {
            label: _('Recos'),
            icon: <DesignServices />,
            onClick: () => {
                setOpen?.(false);
                navigate(reverse('recommendations.List'));
            },
            enabled: true,
        },
        helpCenter: {
            label: _('Ayuda'),
            icon: <Help />,
            onClick: () => {
                setOpen?.(false);
                navigate(reverse('helpCenter.Home'));
            },
            enabled: helpCenterEnabled,
        },

        utilities: {
            label: _('Utilidades'),
            icon: <Construction />,
            onClick: () => {
                setUtilitiesOpen((utilitiesOpen) => {
                    return !utilitiesOpen;
                });
            },
            enabled: true,
        },
        products: {
            label: _('Productos'),
            icon: <Icon type='seedBag' variant='primary' />,
            onClick: () => {
                setOpen?.(false);
                if (isValidatedBayerUserType) {
                    navigate(reverse('productExploration.main'));
                } else {
                    navigate(reverse('catalog.BrandSelection'));
                }
            },
            enabled: true,
        },
    };

    const enabledTabs = PROFILES[validatedType].navTabs;

    return enabledTabs.reduce(
        (acc, key) => {
            const tab = tabs[key];
            if (tab.enabled) {
                acc[key] = tab;
            }
            return acc;
        },
        {} as typeof tabs
    );
};

export const useUtilitiesTabs = ({
    setCreateRecommendationModalOpen,
}: {
    setCreateRecommendationModalOpen: (open: boolean) => void;
}): Record<
    string,
    { label: string; description: string; icon: React.ReactNode; onClick: () => void; enabled: boolean }
> => {
    const navigate = useNavigate();
    const country = useSelector(getSessionCountry);
    const isValidatedBayerUserType = useIsValidatedBayerUserType();
    const isValidatedConsultant = useIsValidatedConsultant();
    const isValidatedSquad = useIsValidatedSquad();
    const bagQualityEnabled = useSelector(getBagQualityEnabled);
    const carbonCalculatorEnabled = useSelector(getCarbonCalculatorEnabled);
    const weedFinderEnabled = useSelector(getEnabledWeedFinder);
    const fieldTrialNetworkEnabled = useSelector(getFieldTrialsNetworkEnabled);
    const askAnExpertEnabled = useSelector(getAskAnExpertEnabled);
    const activitiesEnabled = useSelector(getActivitiesEnabled);

    const firstFieldQuery = useMyFields({ page: 1, pageSize: 1, selectOnly: 'id' });
    const firstFieldId = firstFieldQuery.data?.results?.[0]?.id;
    const activitiesDescription = isValidatedSquad
        ? _('Entrenamientos y encuentros de generación de demanda con productores')
        : _('Registrá entrenamientos y encuentros de generación de demanda con productores');

    return {
        activities: {
            label: _('Actividades'),
            description: activitiesDescription,
            icon: (
                <PermContactCalendar
                    color='primary'
                    sx={{
                        width: '24px',
                        height: '24px',
                    }}
                />
            ),
            onClick: () => {
                navigate(reverse('Activities.List'));
            },
            enabled: activitiesEnabled && (isValidatedConsultant || isValidatedSquad),
        },
        recommendation: {
            label: _('Recomendaciones'),
            description: _('Obtené recomendaciones personalizadas para tus lotes.'),
            icon: (
                <DesignServices
                    color='primary'
                    sx={{
                        width: '24px',
                        height: '24px',
                    }}
                />
            ),
            onClick: () => {
                setCreateRecommendationModalOpen(true);
            },
            enabled: !isValidatedSquad,
        },
        seedQuality: {
            label: _('Calidad de semilla'),
            description: _('Accedé a datos de calidad y sugerencias de placa para un híbrido/grado.'),
            icon: (
                <Icon
                    type='cornDensityRecommendation'
                    variant='primary'
                    sx={{
                        width: '24px',
                        height: '24px',
                    }}
                />
            ),
            onClick: () => {
                navigate(reverse('BagQuality'));
            },
            enabled: bagQualityEnabled && !isValidatedSquad,
        },
        carbonCalculator: {
            label: _('Simulador de rotaciones sustentables'),
            description: _('Evaluá el impacto de tus rotaciones en el suelo.'),
            icon: (
                <RotateLeft
                    color='primary'
                    sx={{
                        width: '24px',
                        height: '24px',
                    }}
                />
            ),
            onClick: () => {
                navigate(reverse('CarbonCalculator', { params: { fieldId: firstFieldId } }));
            },
            enabled: carbonCalculatorEnabled && Boolean(firstFieldId) && !isValidatedSquad,
        },
        weedSearch: {
            label: _('Buscador de malezas'),
            description: _('Identificá malezas con fotos y descripciones.'),
            icon: (
                <Search
                    color='primary'
                    sx={{
                        width: '24px',
                        height: '24px',
                    }}
                />
            ),
            onClick: () => {
                navigate(reverse('WeedFinder'));
            },
            enabled: weedFinderEnabled && !isValidatedSquad,
        },
        trialNetwork: {
            label: _('Red de ensayos'),
            description: _('Explorá resultados y ubicaciones de ensayos Bayer.'),
            icon: (
                <DeviceHub
                    color='primary'
                    sx={{
                        width: '24px',
                        height: '24px',
                    }}
                />
            ),
            onClick: () => {
                navigate(reverse('FieldTrialNetwork'));
            },
            enabled: fieldTrialNetworkEnabled && !isValidatedSquad,
        },
        askAnExpert: {
            label: _('Hablar con un Experto'),
            description: _('Enviá tus consultas directamente a un especialista.'),
            icon: (
                <Chat
                    color='primary'
                    sx={{
                        width: '24px',
                        height: '24px',
                    }}
                />
            ),
            onClick: () => {
                if (country?.code === 'BR') {
                    openWhatsappWeb({
                        phone: '+5511997535194',
                    });
                } else {
                    navigate(reverse('AskAnExpert'));
                }
            },
            enabled: askAnExpertEnabled && !isValidatedBayerUserType,
        },
    };
};
